// frontend/src/pages/dashboard/ProjectComponents/ProjectTable.js
import React, { Fragment } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Box,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
  Badge,
  Button,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Dashboard as DashboardIcon,
  ViewList as ViewListIcon,
  Flag as FlagIcon,
  Task as TaskIcon,
  Sort as SortIcon,  
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,  
  UnfoldMore,
  ViewKanban as ViewKanbanIcon,
} from "@mui/icons-material";
import moment from "moment-timezone";
import { getAvatarUrl } from "../../../utils/imageHelpers";
import ProjectCommentSection from "./ProjectCommentSection";

const ProjectTable = ({
  projects = [],
  handlers,
  authUser,
  hasPermission,
  theme,
  company,
  newCommentText,
  setNewCommentText,
  commentFiles,
  setCommentFiles,
  projectComments,
  commentLoading,
  handleDownload,
  handleImagePreview,
  expandedProjectId,
  isAdmin,
  isMobile,
  isSmallDesktop,
  sortConfig,
  onSort,
}) => {
  const columnVisibility = {
    index: true,
    name: true,
    startDate: !isSmallDesktop,
    endDate: !isSmallDesktop,
    estimatedHours: isAdmin && !isMobile,
    status: true,
    sprint: !isSmallDesktop,
    assigned: !isSmallDesktop,
    client: !isMobile,
    billing: isAdmin && !isSmallDesktop,
    attachments: true,
    actions: true,
  };

  const totalVisibleColumns = Object.values(columnVisibility).filter(Boolean).length;

  const headerCellStyle = {
    fontWeight: 600,
    fontSize: { xs: "0.65rem", md: "0.75rem", lg: "0.8rem" },
    color: "text.secondary",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    py: 1.5,
    px: 1,
    borderBottom: `2px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  };

  const columnWidths = {
    index: { xs: 40, sm: 50, md: 60 },
    name: { min: { xs: 180, md: 220 }, max: 280 },
    startDate: { min: 100, max: 120, width: 110 },
    endDate: { min: 100, max: 120, width: 110 },
    estimatedHours: { min: 85, max: 100, width: 90 },
    status: { min: 85, max: 110, width: 100 },
    sprint: { min: 90, max: 120, width: 100 },
    assigned: { min: 100, max: 140, width: 120 },
    client: { min: 100, max: 140, width: 120 },
    billing: { min: 120, max: 160, width: 140 },
    attachments: { min: 60, max: 80, width: 70 },
    actions: { min: isSmallDesktop ? 90 : 150, max: isSmallDesktop ? 90 : 170, width: isSmallDesktop ? 90 : 150 },
  };

  const TruncatedText = ({ text, maxLength = 30, variant = "caption", ...props }) => {
    const displayText = text?.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    return (
      <Typography variant={variant} component="span" noWrap {...props}>
        {displayText || "—"}
      </Typography>
    );
  };

  const formatDate = (dateString, format = "MMM DD") => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const options = { month: 'short', day: '2-digit' };
    return date.toLocaleDateString('en-US', options);
  };

  const getSortIcon = (field) => {
    const isActive = sortConfig?.field === field;
    const direction = sortConfig?.direction;
    
    if (!isActive || direction === null || direction === undefined) {
      // Default state - neutral icon
      return <SortIcon fontSize="inherit" sx={{ fontSize: "0.875rem", opacity: 0.3 }} />;
    }
    
    // Active sort - show direction
    return direction === 'asc' 
      ? <ArrowUpwardIcon fontSize="inherit" sx={{ fontSize: "0.875rem", opacity: 0.8, color: 'primary.main' }} />
      : <ArrowDownwardIcon fontSize="inherit" sx={{ fontSize: "0.875rem", opacity: 0.8, color: 'primary.main' }} />;
  };

  const formatBillingInfo = (project) => {
    if (!project) return "N/A";
    const currency = project.currency || "USD";
    switch (project.billing_model) {
      case "hourly": return `${project.hourly_rate || "0"} ${currency}/hr`;
      case "fixed": return `${project.fixed_price || "0"} ${currency} (Fixed)`;
      case "monthly": return `${project.fixed_price || "0"} ${currency}/month`;
      case "quarter": return `${project.fixed_price || "0"} ${currency}/quarter`;
      case "half-yearly": return `${project.fixed_price || "0"} ${currency}/6 months`;
      case "yearly": return `${project.fixed_price || "0"} ${currency}/year`;
      case "mixed": return `Mixed (${project.hourly_rate || "0"}/hr + ${project.fixed_price || "0"} fixed)`;
      case "not-applicable": default: return "Not Applicable";
    }
  };

  return (
    <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: "grey.50", '&:hover': { backgroundColor: "grey.50" } }}>
          {columnVisibility.index && (
            <TableCell align="center" sx={{ ...headerCellStyle, width: columnWidths.index.md, minWidth: columnWidths.index.md, maxWidth: columnWidths.index.md }}>#</TableCell>
          )}
          {columnVisibility.name && (
            <TableCell 
              sx={{ ...headerCellStyle, minWidth: columnWidths.name.min.md, maxWidth: columnWidths.name.max, width: columnWidths.name.max, cursor: 'pointer' }}
              onClick={() => onSort('name')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Name
                {getSortIcon('name')}
              </Box>
            </TableCell>
          )}
          {columnVisibility.startDate && (
            <TableCell 
              sx={{ ...headerCellStyle, minWidth: columnWidths.startDate.min, maxWidth: columnWidths.startDate.max, width: columnWidths.startDate.width, cursor: 'pointer' }}
              onClick={() => onSort('start_date')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Start
                {getSortIcon('start_date')}
              </Box>
            </TableCell>
          )}
          {columnVisibility.endDate && (
            <TableCell 
              sx={{ ...headerCellStyle, minWidth: columnWidths.endDate.min, maxWidth: columnWidths.endDate.max, width: columnWidths.endDate.width, cursor: 'pointer' }}
              onClick={() => onSort('end_date')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                End
                {getSortIcon('end_date')}
              </Box>
            </TableCell>
          )}
          {columnVisibility.estimatedHours && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.estimatedHours.min, maxWidth: columnWidths.estimatedHours.max, width: columnWidths.estimatedHours.width }}>Est. Hrs</TableCell>
          )}
          {columnVisibility.status && (
            <TableCell 
              sx={{ ...headerCellStyle, minWidth: columnWidths.status.min, maxWidth: columnWidths.status.max, width: columnWidths.status.width, cursor: 'pointer' }}
              onClick={() => onSort('status')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Status
                {getSortIcon('status')}
              </Box>
            </TableCell>
          )}
          {columnVisibility.sprint && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.sprint.min, maxWidth: columnWidths.sprint.max, width: columnWidths.sprint.width }}>Active Sprint</TableCell>
          )}
          {columnVisibility.assigned && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.assigned.min, maxWidth: columnWidths.assigned.max, width: columnWidths.assigned.width }}>Assigned</TableCell>
          )}
          {columnVisibility.client && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.client.min, maxWidth: columnWidths.client.max, width: columnWidths.client.width }}>Client</TableCell>
          )}
          {columnVisibility.billing && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.billing.min, maxWidth: columnWidths.billing.max, width: columnWidths.billing.width }}>Billing</TableCell>
          )}
          {columnVisibility.attachments && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.attachments.min, maxWidth: columnWidths.attachments.max, width: columnWidths.attachments.width }}>Files</TableCell>
          )}
          {columnVisibility.actions && (
            <TableCell align="right" sx={{ ...headerCellStyle, minWidth: columnWidths.actions.min, maxWidth: columnWidths.actions.max, width: columnWidths.actions.width }}>Actions</TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {projects.map((project, index) => (
          <Fragment key={project.id}>
            <TableRow
              hover
              onClick={() => handlers.onViewDetails(project)}
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.hover" },
                '&:last-child td': { borderBottom: 0 },
                '& .MuiTableCell-root': { boxSizing: 'border-box' }
              }}
            >
              {columnVisibility.index && (
                <TableCell align="center" sx={{ width: columnWidths.index.md, minWidth: columnWidths.index.md, maxWidth: columnWidths.index.md, py: 1.5, fontSize: { xs: "0.75rem", md: "0.875rem" }, fontWeight: 600, color: "text.secondary", boxSizing: 'border-box' }} onClick={(e) => e.stopPropagation()}>
                  {String(index + 1).padStart(2, "0")}
                </TableCell>
              )}
              {columnVisibility.name && (
                <TableCell sx={{ minWidth: columnWidths.name.min.md, maxWidth: columnWidths.name.max, py: 1.5, px: 1, boxSizing: 'border-box' }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Tooltip title={project.name} arrow placement="top">
                      <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ fontSize: { xs: "0.8rem", md: "0.875rem" }, '&:hover': { color: 'primary.main' } }}>
                        {project.name}
                      </Typography>
                    </Tooltip>
                  </Box>
                </TableCell>
              )}
              {columnVisibility.startDate && (
                <TableCell sx={{ minWidth: columnWidths.startDate.min, maxWidth: columnWidths.startDate.max, width: columnWidths.startDate.width, py: 1.5, px: 1, fontSize: { xs: "0.7rem", md: "0.75rem" }, boxSizing: 'border-box' }}>
                  <Typography variant="body2" fontWeight={500}>{project.start_date ? formatDate(project.start_date) : "N/A"}</Typography>
                </TableCell>
              )}
              {columnVisibility.endDate && (
                <TableCell sx={{ minWidth: columnWidths.endDate.min, maxWidth: columnWidths.endDate.max, width: columnWidths.endDate.width, py: 1.5, px: 1, fontSize: { xs: "0.7rem", md: "0.75rem" }, boxSizing: 'border-box' }}>
                  <Typography variant="body2" fontWeight={500}>{project.end_date ? formatDate(project.end_date) : "N/A"}</Typography>
                </TableCell>
              )}
              {columnVisibility.estimatedHours && (
                <TableCell sx={{ minWidth: columnWidths.estimatedHours.min, maxWidth: columnWidths.estimatedHours.max, width: columnWidths.estimatedHours.width, py: 1.5, px: 1, fontSize: { xs: "0.7rem", md: "0.75rem" }, boxSizing: 'border-box' }}>
                  <Typography variant="body2">{project.estimated_hours || "—"}</Typography>
                </TableCell>
              )}
              {columnVisibility.status && (
                <TableCell sx={{ minWidth: columnWidths.status.min, maxWidth: columnWidths.status.max, width: columnWidths.status.width, py: 1.5, px: 1, boxSizing: 'border-box' }}>
                  <Tooltip title={project.statusObj?.name || "Unknown"} arrow>
                    <Chip label={<TruncatedText text={project.statusObj?.name} maxLength={10} variant="caption" />} sx={{ backgroundColor: project.statusObj?.color || "#9CA3AF", color: "#fff", fontSize: { xs: "0.65rem", md: "0.7rem" }, height: 24, maxWidth: "100%", '& .MuiChip-label': { px: 1, textTransform: 'uppercase' } }} size="small" />
                  </Tooltip>
                </TableCell>
              )}
              {columnVisibility.sprint && (
                <TableCell sx={{ minWidth: columnWidths.sprint.min, maxWidth: columnWidths.sprint.max, width: columnWidths.sprint.width, py: 1.5, px: 1, boxSizing: 'border-box' }}>
                  {project.active_sprint ? (
                    <Tooltip title={`${project.active_sprint.start_date} → ${project.active_sprint.end_date}`}>
                      <Chip label={<TruncatedText text={project.active_sprint.name} maxLength={14} variant="caption" sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }} />} size="small" color="primary" variant="outlined" sx={{ maxWidth: "100%", height: 22, fontSize: { xs: "0.6rem", md: "0.65rem" }, '& .MuiChip-label': { px: 0.75, fontSize: { xs: "0.6rem", md: "0.65rem" } } }} />
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>—</Typography>
                  )}
                </TableCell>
              )}
              {columnVisibility.assigned && (
                <TableCell sx={{ minWidth: columnWidths.assigned.min, maxWidth: columnWidths.assigned.max, width: columnWidths.assigned.width, py: 1.5, px: 1, boxSizing: 'border-box' }}>
                  {project.assignedUsers?.length > 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                      {project.assignedUsers.slice(0, isSmallDesktop ? 2 : 3).map((user) => {
                        const avatarUrl = getAvatarUrl(user.avatar);
                        const hasAvatar = !!user.avatar;
                        
                        return (
                          <Tooltip key={user.id} title={user.name} placement="top" arrow>
                            <Avatar
                              sx={{ 
                                width: { xs: 22, md: 24 }, 
                                height: { xs: 22, md: 24 }, 
                                fontSize: { xs: 10, md: 12 }, 
                                border: "2px solid", 
                                borderColor: "background.paper", 
                                cursor: "pointer",
                                bgcolor: hasAvatar ? undefined : theme.palette.primary.light,
                                '& img': {
                                  objectFit: 'cover'
                                }
                              }}
                              src={avatarUrl || undefined}
                              alt={user.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = undefined;
                                // Fallback to initials with primary color
                                e.target.parentElement.style.backgroundColor = theme.palette.primary.light;
                              }}
                            >
                              {user.name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                          </Tooltip>
                        );
                      })}
                      {project.assignedUsers.length > (isSmallDesktop ? 2 : 3) && (
                        <Tooltip title={project.assignedUsers.slice(isSmallDesktop ? 2 : 3).map(u => u.name).join(", ")} arrow>
                          <Chip label={`+${project.assignedUsers.length - (isSmallDesktop ? 2 : 3)}`} size="small" variant="outlined" sx={{ height: { xs: 20, md: 22 }, minWidth: 28, '& .MuiChip-label': { px: 1, fontSize: { xs: "0.6rem", md: "0.65rem" } } }} />
                        </Tooltip>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                  )}
                </TableCell>
              )}
              {columnVisibility.client && (
                <TableCell sx={{ minWidth: columnWidths.client.min, maxWidth: columnWidths.client.max, width: columnWidths.client.width, py: 1.5, px: 1, boxSizing: 'border-box' }}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>{project.client?.name || "—"}</Typography>
                </TableCell>
              )}
              {columnVisibility.billing && (
                <TableCell sx={{ minWidth: columnWidths.billing.min, maxWidth: columnWidths.billing.max, width: columnWidths.billing.width, py: 1.5, px: 1, fontSize: { xs: "0.7rem", md: "0.75rem" }, boxSizing: 'border-box' }}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>{formatBillingInfo(project)}</Typography>
                </TableCell>
              )}
              {columnVisibility.attachments && (
                <TableCell sx={{ minWidth: columnWidths.attachments.min, maxWidth: columnWidths.attachments.max, width: columnWidths.attachments.width, py: 1.5, px: 1, boxSizing: 'border-box' }}>
                  {project.attachments?.length > 0 ? (
                    <Tooltip title={`${project.attachments.length} attachment(s)`}>
                      <Badge badgeContent={project.attachments.length || 0} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
                        <IconButton size="small" sx={{ p: { xs: 0.3, md: 0.5 } }} onClick={(e) => { e.stopPropagation(); handlers.onViewAttachments(project); }}>
                          <AttachFileIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">0</Typography>
                  )}
                </TableCell>
              )}
              {columnVisibility.actions && (
                <TableCell align="right" sx={{ minWidth: columnWidths.actions.min, maxWidth: columnWidths.actions.max, width: columnWidths.actions.width, py: 1.5, px: 1, boxSizing: 'border-box' }} onClick={(e) => e.stopPropagation()}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: { xs: 0.25, md: 0.5 }, flexWrap: "wrap", position: 'relative' }}>
                    {(hasPermission("project.view.all") || project.assignedUsers.some((u) => u.id === authUser?.id) || project.created_by === authUser?.id) && (
                      <Tooltip title="View Details">
                        <IconButton size="small" sx={{ p: { xs: 0.3, md: 0.5 } }} onClick={(e) => { e.stopPropagation(); handlers.onViewDetails(project); }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("project.board.view") && (hasPermission("project.view.all") || project.assignedUsers.some((u) => u.id === authUser?.id)) && (
                      <Tooltip title="Kanban Board">
                        <IconButton size="small" sx={{ p: { xs: 0.3, md: 0.5 } }} onClick={(e) => { e.stopPropagation(); handlers.onViewBoard(project.id); }}>
                          <ViewKanbanIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("backlog.view") && (
                      <Tooltip title="Backlog">
                        <IconButton size="small" color="error.light" sx={{ p: { xs: 0.3, md: 0.5 }, color: 'error.light', '&:hover': { bgcolor: 'error.lighter', color: 'error.main' } }} onClick={(e) => { e.stopPropagation(); handlers.onViewBacklog(project.id); }}>
                          <ViewListIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("sprint.view") && (
                      <Tooltip title="Sprints">
                        <IconButton size="small" sx={{ p: { xs: 0.3, md: 0.5 } }} onClick={(e) => { e.stopPropagation(); handlers.onViewSprints(project.id); }}>
                          <FlagIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("task.view") && (
                      <Tooltip title="Tasks">
                        <IconButton size="small" sx={{ p: { xs: 0.3, md: 0.5 } }} onClick={(e) => { e.stopPropagation(); handlers.onViewTasks(project.id); }}>
                          <TaskIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(hasPermission("project.update") || hasPermission("project.update.status") || hasPermission("project.manage.team")) && (project.assignedUsers.some((u) => u.id === authUser?.id) || project.created_by === authUser?.id) && (
                      <Tooltip title="Edit">
                        <IconButton size="small" sx={{ p: { xs: 0.3, md: 0.5 } }} onClick={(e) => { e.stopPropagation(); handlers.onEdit(project); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("project.delete") && (
                      <Tooltip title="Delete">
                        <IconButton size="small" sx={{ p: { xs: 0.3, md: 0.5 } }} onClick={(e) => { e.stopPropagation(); handlers.onDelete(project.id, project.status); }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("project.comment.add") && (
                      <Tooltip title={expandedProjectId === project.id ? "Hide comments" : "Show comments"}>
                        <Badge badgeContent={project.comment_count || 0} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
                          <IconButton size="small" sx={{ p: { xs: 0.3, md: 0.5 } }} onClick={(e) => { e.stopPropagation(); if (expandedProjectId === project.id) { handlers.onCollapse(project.id); } else { handlers.onExpand(project.id); } }}>
                            <ChatBubbleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Badge>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              )}
            </TableRow>
            {expandedProjectId === project.id && (
              <TableRow>
                <TableCell colSpan={totalVisibleColumns} sx={{ p: 0, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: "action.hover", boxSizing: 'border-box', width: '100%' }}>
                  <Collapse in={expandedProjectId === project.id} timeout="auto" unmountOnExit>
                    <Box sx={{ p: { xs: 2, md: 3 }, maxHeight: { xs: "400px", md: "500px" }, overflowY: "auto", '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: theme.palette.grey[400], borderRadius: 3 } }}>
                      <ProjectCommentSection
                        projectId={project.id}
                        comments={projectComments[project.id] || []}
                        loading={commentLoading[project.id]}
                        onPostComment={handlers.onPostComment}
                        onDeleteComment={handlers.onDeleteComment}
                        users={project.assignedUsers || []}
                        authUser={authUser}
                        newCommentText={newCommentText}
                        setNewCommentText={setNewCommentText}
                        commentFiles={commentFiles}
                        setCommentFiles={setCommentFiles}
                        theme={theme}
                        hasPermission={hasPermission}
                        handleDownload={handleDownload}
                        handleImagePreview={handleImagePreview}
                      />
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            )}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );
};

export default ProjectTable;